import is from '@sindresorhus/is';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { JobArchitecturePath } from '~/shared/data-contract/job-architecture';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { writeUrlTileSearchParams } from '../../url-tiles';
import { createJsaClusterDetailTilePayload, createJsaProfileDetailTilePayload } from '../url-tile-payloads';
import { jobArchitectureUrlTileDefinitions } from '../url-tiles/tile-definitions';

type NavigateToJobArchitectureItemParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly path: JobArchitecturePath;
    readonly preferredLanguage?: LanguageCodeForJSA;
};

type UseLocateJobArchitectureItemReturn = {
    readonly navigate: (params: NavigateToJobArchitectureItemParams) => void;
};

export const buildJobRoleParams = (
    architectureId: string,
    itemId: string,
    path: JobArchitecturePath,
    searchParams: URLSearchParams,
    preferredLanguage?: LanguageCodeForJSA,
    version = 1,
): URLSearchParams => {
    if (!is.nonEmptyString(path.family) || !is.nonEmptyString(path.cluster)) {
        throw new Error('Cannot build job role params: missing required parent (family or cluster)');
    }

    const params = writeUrlTileSearchParams({
        definitions: jobArchitectureUrlTileDefinitions,
        payloads: [
            createJsaProfileDetailTilePayload({
                architectureId,
                itemType: 'job-role',
                mode: 'view',
                preferredLanguage,
                profileId: itemId,
                version,
            }),
        ],
        searchParams,
    });

    // Write the board selection together with the tile so the target URL is
    // complete on arrival; otherwise the selection only exists as transient
    // client state and is lost on the next router navigation.
    params.set('family', path.family);
    params.set('cluster', path.cluster);
    params.set('role', itemId);
    params.delete('level');

    return params;
};

export const buildJobLevelParams = (
    architectureId: string,
    itemId: string,
    path: JobArchitecturePath,
    searchParams: URLSearchParams,
    preferredLanguage?: LanguageCodeForJSA,
    version = 1,
): URLSearchParams => {
    if (!is.nonEmptyString(path.family) || !is.nonEmptyString(path.cluster) || !is.nonEmptyString(path.role)) {
        throw new Error('Cannot build job level params: missing required parent (family, cluster, or role)');
    }

    const params = writeUrlTileSearchParams({
        definitions: jobArchitectureUrlTileDefinitions,
        payloads: [
            createJsaProfileDetailTilePayload({
                architectureId,
                itemType: 'job-level',
                mode: 'view',
                preferredLanguage,
                profileId: itemId,
                version,
            }),
        ],
        searchParams,
    });

    params.set('family', path.family);
    params.set('cluster', path.cluster);
    params.set('role', path.role);
    params.set('level', itemId);

    return params;
};

export const useLocateJobArchitectureItem = (): UseLocateJobArchitectureItemReturn => {
    const routerNavigate = useNavigate();

    const navigate = useCallback(
        ({ architectureId, itemId, itemType, path, preferredLanguage }: NavigateToJobArchitectureItemParams) => {
            // Selection params are written by nuqs through history.replaceState,
            // which react-router does not observe — its useSearchParams would hand
            // us a stale copy without them. Read the live URL instead.
            const searchParams = new URLSearchParams(window.location.search);
            const params =
                itemType === 'job-role'
                    ? buildJobRoleParams(architectureId, itemId, path, searchParams, preferredLanguage)
                    : buildJobLevelParams(architectureId, itemId, path, searchParams, preferredLanguage);

            routerNavigate(`/job-architecture-v2/${architectureId}/overview?${params.toString()}`);
        },
        [routerNavigate],
    );

    return { navigate };
};

type NavigateToJobItemParentPageParams = {
    readonly architectureId: string;
    readonly path: JobArchitecturePath;
};

type UseNavigateToJobItemParentPageReturn = {
    readonly navigate: (params: NavigateToJobItemParentPageParams) => void;
};

export const useNavigateToJobItemParentPage = (): UseNavigateToJobItemParentPageReturn => {
    const routerNavigate = useNavigate();

    const navigate = useCallback(
        ({ architectureId, path }: NavigateToJobItemParentPageParams) => {
            if (!is.nonEmptyString(path.family) || !is.nonEmptyString(path.cluster)) {
                routerNavigate(`/job-architecture-v2/${architectureId}/overview`);
                return;
            }

            const params = writeUrlTileSearchParams({
                definitions: jobArchitectureUrlTileDefinitions,
                payloads: [
                    createJsaClusterDetailTilePayload({
                        architectureId,
                        clusterId: path.cluster,
                        familyId: path.family,
                        mode: 'view',
                    }),
                ],
                searchParams: new URLSearchParams(),
            });

            params.set('family', path.family);
            params.set('cluster', path.cluster);

            routerNavigate(`/job-architecture-v2/${architectureId}/overview?${params.toString()}`);
        },
        [routerNavigate],
    );

    return { navigate };
};
